import { weightedLength } from '@asp/contracts';
import type { XCapabilityProfile } from '@asp/contracts';
import { useDictionary } from '../i18n/context.js';

// ---------------------------------------------------------------------------
// The character counter. Counts with the exact same `weightedLength` the
// server validates with (@asp/contracts) -- never a reimplementation -- so
// the browser and the server can never disagree about whether a post fits.
// Always states its unit ("254 / 280 weighted"), never a bare count: design
// doc section 11.9 requires a counter to say what it is counting so a
// weighted count is never mistaken for a character count.
//
// `profile` is typed `XCapabilityProfile`, not the broader
// `CapabilityProfile` union: this counts a single free-text field against a
// single max, which is X's shape (`profile.text.max`), not Pinterest's
// (separate `title`/`description` maxes, no combined "text"). Every call
// site today (review.tsx) only ever passes `X_CAPABILITY` against
// `revision.content.text`, itself still X-only (VariantDetail in
// api/client.ts) -- a Pinterest review screen needs its own counter(s), not
// this one widened.
// ---------------------------------------------------------------------------

export type CounterProps = {
  text: string;
  profile: XCapabilityProfile;
  /** Fraction of `max` at which the counter switches to its warning state before actually going over. */
  warnAt?: number;
};

export type CounterState = 'ok' | 'warn' | 'over';

export const Counter = ({ text, profile, warnAt = 0.9 }: CounterProps): React.JSX.Element => {
  const dict = useDictionary();
  const count = profile.text.unit === 'weighted' ? weightedLength(text) : [...text].length;
  const max = profile.text.max;
  const unit = profile.text.unit === 'weighted' ? dict.ui.counter.weighted : dict.ui.counter.codepoints;

  const state: CounterState = count > max ? 'over' : count >= max * warnAt ? 'warn' : 'ok';
  const className = state === 'over' ? 'counter counter--over' : state === 'warn' ? 'counter counter--warn' : 'counter';

  return (
    <span className={className} data-state={state}>{`${count} / ${max} ${unit}`}</span>
  );
};
